import React, {useCallback, useEffect, useState} from 'react';
import {Box, Chip, CircularProgress, IconButton, Modal, Typography, useMediaQuery, useTheme} from '@mui/material';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Plein écran d'une photo avec navigation, rotation et téléchargement.
 * Extrait de `PhotoCarousel` (sa Modal) pour être réutilisé sans le carrousel (INT-02).
 *
 * @param {Array<{src: string, label?: string, color?: string}>} photos  chemins relatifs (sans base)
 * @param {number|null} index  index ouvert, null = fermé
 * @param {string} assetsUrl   base des photos (Constants.API_ASSETS_PHOTO_INTERVENTION)
 */
function PhotoLightbox({photos = [], index, onClose, onIndexChange, assetsUrl}) {
    const [rotation, setRotation] = useState(0);
    const [loaded, setLoaded] = useState({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const open = index != null && photos[index] != null;
    const current = open ? photos[index] : null;
    const hasNav = photos.length > 1;
    // Voisines préchargées (balises img cachées) pour une navigation sans latence S3.
    const neighbours = hasNav ? [photos[(index + 1) % photos.length], photos[(index - 1 + photos.length) % photos.length]] : [];

    const go = useCallback((delta) => {
        if (!hasNav) return;
        const next = (index + delta + photos.length) % photos.length;
        setRotation(0);
        onIndexChange?.(next);
    }, [hasNav, index, photos.length, onIndexChange]);

    useEffect(() => {
        setRotation(0);
    }, [index]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') go(-1);
            if (e.key === 'ArrowRight') go(1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, go]);

    const handleDownload = () => {
        if (!current) return;
        const link = document.createElement('a');
        link.href = `${assetsUrl}/${current.src}`;
        link.download = current.src.split('/').pop();
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="photo-lightbox" closeAfterTransition>
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    bgcolor: '#fff', p: isMobile ? 1 : 2, borderRadius: 3, boxShadow: 24, outline: 'none',
                    maxWidth: isMobile ? '95%' : '90%', maxHeight: isMobile ? '95vh' : '90vh',
                    width: isMobile ? '95%' : 'auto', height: isMobile ? 'auto' : '90%', overflow: 'hidden',
                }}
            >
                <Box sx={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {current?.label && (
                            <Chip size="small" label={current.label.toUpperCase()}
                                  sx={{bgcolor: current.color || '#0F2D69', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '.06em'}}/>
                        )}
                        {hasNav && <Typography variant="caption" color="text.secondary">{index + 1} / {photos.length}</Typography>}
                    </Box>
                    <IconButton size="small" onClick={onClose} aria-label="Fermer"><CloseIcon/></IconButton>
                </Box>

                <Box sx={{flexGrow: 1, minHeight: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, overflow: 'hidden'}}>
                    {hasNav && (
                        <IconButton onClick={() => go(-1)} aria-label="Photo précédente"><ChevronLeftIcon/></IconButton>
                    )}
                    {current && !loaded[current.src] && (
                        <CircularProgress size={36} sx={{position: 'absolute', color: '#0F2D69'}}/>
                    )}
                    {current && (
                        <img
                            src={`${assetsUrl}/${current.src}`}
                            alt={current.label || 'Photo intervention'}
                            onLoad={() => setLoaded((l) => (l[current.src] ? l : {...l, [current.src]: true}))}
                            style={{
                                maxWidth: '100%', maxHeight: '100%', borderRadius: 12,
                                transform: `rotate(${rotation}deg)`, transition: 'transform 0.3s ease', objectFit: 'contain',
                            }}
                        />
                    )}
                    {hasNav && (
                        <IconButton onClick={() => go(1)} aria-label="Photo suivante"><ChevronRightIcon/></IconButton>
                    )}
                </Box>

                {neighbours.map((p) => (
                    <img key={p.src} src={`${assetsUrl}/${p.src}`} alt="" aria-hidden="true" style={{display: 'none'}}/>
                ))}

                <Box sx={{mt: 1.5, display: 'flex', gap: 1, flexShrink: 0}}>
                    <Button variant="outlined" onClick={() => setRotation((r) => (r + 90) % 360)}>Tourner</Button>
                    <Button variant="outlined" color="secondary" onClick={handleDownload}>Télécharger</Button>
                </Box>
            </Box>
        </Modal>
    );
}

export default PhotoLightbox;
